import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useParams } from 'react-router-dom';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const ViewReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();

  const [report, setReport] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchReportDetails();
  }, [id]);

  const fetchReportDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/reports/excursions/${id}`, {
        headers: {
          token: token,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (data.success) {
        setReport(data.data.excursion);
        setPassengers(data.data.passengers || []);
      } else {
        setAlert({ show: true, message: data.message || 'Error fetching report details', type: 'error' });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report details:', error);
      setAlert({ show: true, message: 'Error fetching report details', type: 'error' });
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Helper functions for chip colors

  const getPaymentColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPaymentFlowColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid with qr':
        return 'success';
      case 'paid no qr':
        return 'info';
      case 'paid but no show':
        return 'warning';
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  // Passenger columns for DataGrid
  const passengerColumns = [
    {
      field: 'name',
      headerName: 'Passenger (Last + First)',
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.8,
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment',
      flex: 1,
      renderCell: (params) => (
        <Chip label={params.value?.toUpperCase() || 'N/A'} color={getPaymentColor(params.value)} size="small" />
      ),
    },
    {
      field: 'scanStatusLabel',
      headerName: 'Scan / Not scanned',
      flex: 1,
      renderCell: (params) => {
        const scanned = Number(params.row.scanStatus || 0);
        const total = Number(params.row.quantity || 1);
        const label = params.value || `${scanned} / ${total} Scanned`;
        const isFullyScanned = total > 0 && scanned >= total;
        const isPartial = scanned > 0 && scanned < total;
        return (
          <Chip
            label={label}
            color={isFullyScanned ? 'success' : isPartial ? 'warning' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'busNumber',
      headerName: 'Bus number',
      flex: 0.8,
    },
    {
      field: 'view',
      headerName: 'View',
      sortable: false,
      filterable: false,
      width: 80,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            const bookingId = params.row.bookingId || params.row.BookingID || params.row.booking_id;
            if (bookingId) navigate(`/booking/${bookingId}`);
          }}
          size="small"
          sx={{
            backgroundColor: colors.blueAccent[600],
            color: colors.grey[100],
            borderRadius: 2,
            '&:hover': { backgroundColor: colors.blueAccent[700] },
          }}
          title="View Booking"
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box m="20px">
        <Alert severity="error">Report not found</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Header */}
      <Box display="flex" alignItems="center" mb="20px">
        <IconButton onClick={() => navigate('/reports/excursion')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h2" color={colors.grey[100]} fontWeight="bold">
          View Report
        </Typography>
      </Box>

      {/* Report Summary Cards */}
      <Grid container spacing={3} mb="30px">
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Total Booked
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.greenAccent[500]}>
                    {report.totalBooked}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: colors.greenAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Attended
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.blueAccent[500]}>
                    {report.totalAttended}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Paid
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.greenAccent[500]}>
                    {report.totalPaid}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: colors.greenAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color={colors.grey[100]} gutterBottom variant="h6">
                    Unpaid
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={colors.redAccent[500]}>
                    {report.totalUnpaid}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: colors.redAccent[500] }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* QR Code and Payment Flow Summary Cards */}
      <Grid container spacing={3} mb="30px"></Grid>

      {/* Excursion Details */}
      <Card sx={{ backgroundColor: colors.primary[400], mb: '30px' }}>
        <CardContent>
          <Typography variant="h4" color={colors.grey[100]} fontWeight="bold" mb="20px">
            Excursion Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Destination Name
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {report.departureName}
              </Typography>

              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Excursion Name
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {report.excursionName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Departure Date
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                {formatDate(report.departDate)}
              </Typography>

              <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                Excursion Price
              </Typography>
              <Typography variant="body1" color={colors.grey[100]} mb="15px">
                €{report.price}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs for Passenger Details */}
      <Card sx={{ backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: colors.grey[300],
                  fontWeight: 'bold',
                  fontSize: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  '&.Mui-selected': {
                    color: colors.greenAccent[400],
                    backgroundColor: colors.blueAccent[800],
                    borderRadius: '8px 8px 0 0',
                  },
                  '&:hover': {
                    color: colors.greenAccent[300],
                    backgroundColor: colors.blueAccent[700],
                    borderRadius: '8px 8px 0 0',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: colors.greenAccent[400],
                  height: '3px',
                  borderRadius: '2px',
                },
              }}
            >
              <Tab label="Passenger Details" />
              <Tab label="Payment Summary" />
            </Tabs>
          </Box>

          {/* Passenger Details Tab */}
          {tabValue === 0 && (
            <Box mt="20px">
              <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb="20px">
                Passenger Details
              </Typography>
              <Box
                height="400px"
                sx={{
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { borderBottom: 'none' },
                  '& .name-column--cell': { color: colors.greenAccent[300] },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.blueAccent[700],
                    borderBottom: 'none',
                  },
                  '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: 'none',
                    backgroundColor: colors.blueAccent[700],
                  },
                  '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
                  '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
                    color: `${colors.grey[100]} !important`,
                    fontSize: 14,
                  },
                }}
              >
                <DataGrid
                  rows={passengers.map((passenger, index) => ({
                    ...passenger,
                    id: passenger.id || passenger.bookingId || `passenger-${index}`,
                  }))}
                  columns={passengerColumns}
                  getRowId={(row) => row.id}
                  components={{ Toolbar: CustomToolbar }}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  pagination
                  sortingOrder={['asc', 'desc']}
                />
              </Box>
            </Box>
          )}

          {/* Payment Summary Tab */}
          {tabValue === 1 && (
            <Box mt="20px">
              <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb="20px">
                Payment Summary
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center" p={3} sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 2 }}>
                    <Typography variant="h4" color={colors.greenAccent[500]} fontWeight="bold">
                      €{report.totalPaid * report.price}
                    </Typography>
                    <Typography variant="h6" color={colors.grey[100]}>
                      Total Paid
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center" p={3} sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 2 }}>
                    <Typography variant="h4" color={colors.redAccent[500]} fontWeight="bold">
                      €{report.totalUnpaid * report.price}
                    </Typography>
                    <Typography variant="h6" color={colors.grey[100]}>
                      Total Unpaid
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center" p={3} sx={{ backgroundColor: colors.blueAccent[700], borderRadius: 2 }}>
                    <Typography variant="h4" color={colors.blueAccent[500]} fontWeight="bold">
                      €{(report.totalPaid + report.totalUnpaid) * report.price}
                    </Typography>
                    <Typography variant="h6" color={colors.grey[100]}>
                      Total Revenue
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Alert */}
      {alert.show && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert({ show: false, message: '', type: 'success' })}
          sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
        >
          {alert.message}
        </Alert>
      )}
    </Box>
  );
};

export default ViewReport;
